// Provider-agnostic softphone facade. Two backends share one event surface:
//   - TwilioBackend: WebRTC via @twilio/voice-sdk (server-bridged outbound)
//   - SipBackend:    WebRTC via sip.js, registered against Asterisk's WSS
//                    transport — outbound calls cross the VP SIP trunk
//
// In mock mode (Twilio creds absent or SIP gateway not configured) the
// matching backend simulates a call lifecycle locally so the UI is still
// exercisable end-to-end.

import { Device, type Call as TwilioCall } from '@twilio/voice-sdk';
import {
  Invitation,
  Inviter,
  Registerer,
  RegistererState,
  SessionState,
  UserAgent,
  type Session,
  type UserAgentOptions,
} from 'sip.js';
import { api } from '../lib/api';
import { extractApiErrorMessage } from '../lib/api-error';
import { getDeviceId } from '../lib/device-id';
import { useSoftphone } from '../store/softphone';

export type SoftphoneEvent =
  | { type: 'registered' }
  | { type: 'unregistered' }
  | { type: 'error'; message: string }
  | { type: 'incoming'; call: TwilioCall; from: string }
  | { type: 'sip_incoming'; from: string }
  | { type: 'incoming_canceled' }
  | { type: 'connecting' }
  | { type: 'connected' }
  | { type: 'disconnected'; reason?: string }
  | { type: 'mute_changed'; muted: boolean };

type Listener = (e: SoftphoneEvent) => void;

interface TokenResponse {
  token: string;
  identity: string;
  mock: boolean;
  edge?: string;
}

interface DialOptions {
  toRaw: string;
  leadId?: string;
  campaignId?: string;
  callbackId?: string;
  callerId?: string;
  callerProvider?: 'TWILIO' | 'VOIPLINE';
}

interface DialResult {
  ok: boolean;
  callId?: string;
  reason?: string;
  detail?: string;
}

/** dial() reason for "the agent hit End call before the call connected". Not a
 *  fault — callers should drop the attempt quietly rather than raise an error. */
export const CANCELED_BEFORE_CONNECT = 'CANCELED_BEFORE_CONNECT';

interface SipCredentials {
  mock: boolean;
  wssUrl: string;
  sipDomain: string;
  sipUser: string;
  sipPassword: string;
  displayName: string;
  scope: 'agent' | 'shared';
}

class SoftphoneService {
  // ── Twilio backend state ───────────────────────────────────────────────
  private device: Device | null = null;
  private call: TwilioCall | null = null;
  private mockMode = false;
  private mockTimers: ReturnType<typeof setTimeout>[] = [];
  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  // ── SIP backend state ──────────────────────────────────────────────────
  private sipUserAgent: UserAgent | null = null;
  private sipRegisterer: Registerer | null = null;
  private sipSession: Session | null = null;
  private sipMockMode = false;
  private sipAudioElement: HTMLAudioElement | null = null;
  private sipMuted = false;
  // Tracks the Call.id for the active SIP session so we can POST
  // /calls/:id/answered when the WebRTC session reaches Established.
  private sipActiveCallId: string | null = null;
  // Holds the callId sent by the server in incoming_call_preview before the
  // SIP INVITE arrives. onInvite reads and consumes it so sipActiveCallId is
  // set correctly for inbound calls without needing a custom SIP header.
  private pendingInboundCallId: string | null = null;
  // SIP INVITE waiting for the agent to Accept or Reject. Cleared once the
  // user picks an option, or when the caller cancels and the invitation
  // transitions to Terminated before we acted on it.
  private sipPendingInvitation: Invitation | null = null;

  // Per-call listener bookkeeping. Without these, every fresh call adds
  // handlers that the underlying provider (Twilio Call / sip.js Session)
  // never sheds — after a few calls the page accumulates dozens of stale
  // listeners and their MediaStreams, eventually freezing the tab.
  private callHandlers: Array<[string, (...args: unknown[]) => void]> = [];
  private sipSessionListener: ((s: SessionState) => void) | null = null;

  // True from the moment a dial starts until a provider session actually
  // exists — across /calls/initiate, SIP registration, and (for Twilio) the
  // ring window before the staff leg is bridged back to us as an `incoming`.
  // The End call button is on screen for all of it thanks to the optimistic
  // 'connecting' status, but there is nothing to hang up, so the click used to
  // be a silent no-op and the call went live moments later behind a dialer
  // that had already returned to wrap-up. Cleared by attachCall /
  // attachSipSession, whose handles hangup() can act on directly.
  private awaitingProviderSession = false;
  private dialCanceledAt: number | null = null;
  // The cancel only applies to the dial it interrupted. Bound it in time so a
  // stale flag can't reject a genuine inbound bridge minutes later — Twilio
  // bridges the staff leg within a second or two of /calls/initiate.
  private static readonly DIAL_CANCEL_WINDOW_MS = 15_000;

  private isDialCanceled(): boolean {
    if (this.dialCanceledAt === null) return false;
    if (Date.now() - this.dialCanceledAt > SoftphoneService.DIAL_CANCEL_WINDOW_MS) {
      this.dialCanceledAt = null;
      return false;
    }
    return true;
  }

  private listeners = new Set<Listener>();

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Called by Softphone.tsx when incoming_call_preview arrives for a VP
   *  inbound call. Stores the callId so the onInvite SIP handler can bind it
   *  to sipActiveCallId before accepting the INVITE. */
  setIncomingCallId(callId: string | null): void {
    this.pendingInboundCallId = callId;
  }

  private emit(event: SoftphoneEvent): void {
    this.listeners.forEach((l) => l(event));
  }

  async initialize(): Promise<void> {
    // Twilio backend always initialises (the page may have Twilio caller IDs
    // in the dropdown). The SIP backend lazy-initialises on first VP
    // dial — registering against Asterisk eagerly would burn a SIP slot for
    // every browser tab even when the agent never makes a VP call.
    const tokenData = await api.post<TokenResponse>('/voice/token', { deviceId: getDeviceId() });
    this.mockMode = !!tokenData.mock;

    if (this.mockMode) {
      this.emit({ type: 'registered' });
      return;
    }

    this.device = new Device(tokenData.token, {
      logLevel: 1,
      sounds: {},
      ...(tokenData.edge ? { edge: tokenData.edge } : {}),
    });

    this.device.on('registered', () => this.emit({ type: 'registered' }));
    this.device.on('unregistered', () => this.emit({ type: 'unregistered' }));
    this.device.on('error', (err) => this.emit({ type: 'error', message: err.message }));

    this.device.on('incoming', (incoming) => {
      // An End call pressed during the dial round-trip lands before Twilio
      // bridges the prospect's leg back to us. Reject it — auto-accepting
      // would put the agent on a live call the dialer no longer knows about.
      if (this.isDialCanceled()) {
        this.dialCanceledAt = null;
        try { incoming.reject(); } catch { /* ignore */ }
        return;
      }
      this.attachCall(incoming);
      const from = (incoming.parameters as Record<string, string>).From ?? '';
      this.emit({ type: 'incoming', call: incoming, from });
      // Auto-accept: all incoming events on a staff client are server-initiated
      // outbound bridges — there is no manual answer step in the UI.
      incoming.accept();
    });

    this.device.on('tokenWillExpire', () => {
      void this.refreshToken();
    });

    await this.device.register();
    this.scheduleTokenRefresh();

    // Attempt SIP registration in the background so the agent's browser is
    // ready to receive inbound VP calls without placing an outbound call
    // first. Non-fatal: agents without SIP credentials get a silent no-op
    // (the server returns 503 which ensureSipRegistered catches internally).
    void this.ensureSipRegistered();
  }

  private scheduleTokenRefresh(): void {
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer);
    this.tokenRefreshTimer = setTimeout(() => void this.refreshToken(), 50 * 60 * 1000);
  }

  private async refreshToken(): Promise<void> {
    try {
      const tokenData = await api.post<TokenResponse>('/voice/token', { deviceId: getDeviceId() });
      if (this.device && !this.mockMode) {
        this.device.updateToken(tokenData.token);
      }
      this.scheduleTokenRefresh();
    } catch (err) {
      this.emit({ type: 'error', message: 'Failed to refresh Twilio token' });
    }
  }

  private async ensureMicrophoneAvailable(): Promise<{ ok: boolean; detail?: string }> {
    if (!navigator.mediaDevices?.getUserMedia) {
      return {
        ok: false,
        detail: 'Microphone access is unavailable in this browser. Use HTTPS and a browser with WebRTC support.',
      };
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return { ok: true };
    } catch (err) {
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        return { ok: false, detail: 'No microphone was found. Connect or enable an input device, then try again.' };
      }
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        return { ok: false, detail: 'Microphone permission was denied. Allow microphone access for this site, then try again.' };
      }
      return { ok: false, detail: 'Could not open the microphone. Check browser, OS, and audio device settings.' };
    }
  }

  // ── SIP backend ────────────────────────────────────────────────────────

  private async ensureSipRegistered(): Promise<{ ok: boolean; detail?: string }> {
    if (this.sipUserAgent && this.sipRegisterer?.state === RegistererState.Registered) {
      return { ok: true };
    }
    let creds: SipCredentials;
    try {
      creds = await api.post<SipCredentials>('/voice/sip-credentials', {});
    } catch (err) {
      return { ok: false, detail: extractApiErrorMessage(err, 'VP SIP credentials are not available') };
    }
    this.sipMockMode = !!creds.mock;
    if (this.sipMockMode) return { ok: true };

    if (!this.sipAudioElement) {
      this.sipAudioElement = document.createElement('audio');
      this.sipAudioElement.autoplay = true;
      this.sipAudioElement.style.display = 'none';
      document.body.appendChild(this.sipAudioElement);
    }

    const uri = UserAgent.makeURI(`sip:${creds.sipUser}@${creds.sipDomain}`);
    if (!uri) return { ok: false, detail: 'Could not parse SIP URI from credentials' };

    const options: UserAgentOptions = {
      uri,
      transportOptions: { server: creds.wssUrl },
      authorizationUsername: creds.sipUser,
      authorizationPassword: creds.sipPassword,
      displayName: creds.displayName,
      logLevel: 'warn',
    };

    try {
      const ua = new UserAgent(options);

      // Wire incoming SIP call handler before ua.start() so no INVITE is missed.
      // The server emits incoming_call_preview (with callId) over the socket
      // before originating the agent's SIP channel, so pendingInboundCallId is
      // populated by the time this fires.
      ua.delegate = {
        onInvite: (invitation: Invitation) => {
          this.sipActiveCallId = this.pendingInboundCallId;
          this.pendingInboundCallId = null;
          this.sipPendingInvitation = invitation;

          // If the caller cancels (or a sibling tab wins a forked INVITE)
          // before the agent picks Accept/Reject, the invitation transitions
          // to Terminated. Clear pending state so the UI returns to ready.
          // Self-removes once the invitation either Establishes (agent
          // accepted — attachSipSession takes over) or Terminates, so
          // back-to-back inbounds don't leave dead listeners on this UA.
          const onInviteState = (state: SessionState): void => {
            if (state === SessionState.Terminated && this.sipPendingInvitation === invitation) {
              this.sipPendingInvitation = null;
              this.sipActiveCallId = null;
              this.emit({ type: 'incoming_canceled' });
            }
            if (state === SessionState.Terminated || state === SessionState.Established) {
              try { invitation.stateChange.removeListener(onInviteState); } catch { /* ignore */ }
            }
          };
          invitation.stateChange.addListener(onInviteState);

          const remoteUri = invitation.remoteIdentity?.uri as { user?: string } | undefined;
          const from = remoteUri?.user ?? '';
          this.emit({ type: 'sip_incoming', from });
        },
      };

      await ua.start();
      const registerer = new Registerer(ua);
      registerer.stateChange.addListener((state: RegistererState) => {
        if (state === RegistererState.Registered) {
          this.emit({ type: 'registered' });
        } else if (state === RegistererState.Unregistered) {
          this.emit({ type: 'unregistered' });
        }
      });
      await registerer.register();
      this.sipUserAgent = ua;
      this.sipRegisterer = registerer;
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'SIP registration failed';
      return { ok: false, detail: message };
    }
  }

  private attachSipSession(session: Session): void {
    // If acceptIncoming re-binds the same Invitation that already had a
    // listener from onInvite, drop that one first — otherwise both fire.
    this.detachSipSession();
    this.sipSession = session;
    // A real handle exists now — hangup() can terminate it directly.
    this.awaitingProviderSession = false;
    console.log('[softphone] attachSipSession state=', session.state);
    const listener = (state: SessionState): void => {
      console.log('[softphone] sip session state →', state);
      switch (state) {
        case SessionState.Establishing:
          this.emit({ type: 'connecting' });
          break;
        case SessionState.Established:
          this.bindSipMedia(session);
          this.emit({ type: 'connected' });
          // Mirror Twilio's status-callback: tell the server the call is up
          // so the supervisor view, Call.answeredAt, and durationSeconds all
          // line up. Surface failures to the console so we can see auth /
          // route problems instead of silently dropping the timestamp.
          if (this.sipActiveCallId) {
            void api.post(`/calls/${this.sipActiveCallId}/answered`, {}).catch((err) => {
              console.warn('[softphone] failed to POST /calls/:id/answered', err);
            });
          }
          break;
        case SessionState.Terminated: {
          // Persist the end state regardless of who hung up. Without this,
          // remote-initiated hangups (prospect hangs up, carrier drops,
          // network blip) leave the row stuck at status='in_progress' with
          // durationSeconds=0 because hangup() only fires from the UI's
          // Hangup button.
          const endedCallId = this.sipActiveCallId;
          try { session.stateChange.removeListener(listener); } catch { /* ignore */ }
          if (this.sipSessionListener === listener) this.sipSessionListener = null;
          this.sipSession = null;
          this.sipMuted = false;
          this.sipActiveCallId = null;
          // Belt-and-braces: explicitly stop the remote tracks. sip.js usually
          // closes the peer connection on Terminated, but if it lags (or the
          // session terminated abnormally) the bound MediaStream can keep
          // playing prospect audio after the disposition modal has opened.
          this.stopRemoteAudioPlayback();
          this.emit({ type: 'disconnected' });
          if (endedCallId) {
            void api.post(`/calls/${endedCallId}/end`).catch((err) => {
              console.warn('[softphone] failed to POST /calls/:id/end', err);
            });
          }
          break;
        }
      }
    };
    session.stateChange.addListener(listener);
    this.sipSessionListener = listener;
    // Belt-and-braces: if invite() (or accept()) raced ahead of attachment
    // and the session is already past Initial, the listener would otherwise
    // miss Establishing/Established and the UI would sit on DialPad while
    // audio is bridged. Replay the current state so the UI catches up.
    if (session.state !== SessionState.Initial) {
      console.log('[softphone] attachSipSession replaying state=', session.state);
      listener(session.state);
    }
  }

  private detachSipSession(): void {
    if (this.sipSession && this.sipSessionListener) {
      try { this.sipSession.stateChange.removeListener(this.sipSessionListener); } catch { /* ignore */ }
    }
    this.sipSessionListener = null;
  }

  private stopRemoteAudioPlayback(): void {
    if (!this.sipAudioElement) return;
    const stream = this.sipAudioElement.srcObject as MediaStream | null;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    this.sipAudioElement.srcObject = null;
  }

  /** Best-effort teardown of the live SIP session, whatever state it is in.
   *  Every verb runs detached from the caller: sip.js rejects (or throws
   *  synchronously) whenever the session moved on between our state read and
   *  the verb landing — the classic case being the remote answering mid-cancel
   *  — and an unresponsive transport leaves bye() pending for the full ~32 s
   *  transaction timeout. Neither may hold up the UI, and neither may leave a
   *  live session orphaned: we drop the handle and the state listener here, so
   *  if the first verb fails nothing else can reach the session to stop it.
   *  Hence the re-check after each attempt, ending with dispose() to close the
   *  peer connection even when signalling won't cooperate. */
  private terminateSipSession(): void {
    const session = this.sipSession;
    if (!session) return;
    this.detachSipSession();
    this.sipSession = null;

    const attempt = async (fn: () => unknown): Promise<void> => {
      try {
        await Promise.resolve(fn());
      } catch {
        /* wrong state / transport gone — the state re-checks below decide what to try next */
      }
    };

    void (async () => {
      if (session.state === SessionState.Initial || session.state === SessionState.Establishing) {
        const pending = session as Partial<Inviter> & Partial<Invitation>;
        // Outbound (Inviter) cancels the INVITE; an inbound Invitation the
        // agent accepted but that hasn't established yet can only be rejected.
        if (typeof pending.cancel === 'function') await attempt(() => pending.cancel!());
        else if (typeof pending.reject === 'function') await attempt(() => pending.reject!());
      }
      if (session.state === SessionState.Established) await attempt(() => session.bye());
      if (session.state !== SessionState.Terminated) {
        await attempt(() => (session as { dispose?: () => Promise<void> }).dispose?.());
      }
    })();
  }

  /** Force-end whichever provider session is currently active without going
   *  through the full hangup() round-trip. Used when the server reports the
   *  call as ended but our local session is still bridged — without this the
   *  prospect's audio keeps playing under the disposition modal. */
  forceEndActiveSession(): void {
    if (this.call) {
      try { this.call.disconnect(); } catch { /* ignore */ }
      this.detachCall();
    }
    this.terminateSipSession();
    this.sipActiveCallId = null;
    this.sipMuted = false;
    this.stopRemoteAudioPlayback();
  }

  private bindSipMedia(session: Session): void {
    if (!this.sipAudioElement) return;
    const sessionWithDescription = session as Session & {
      sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection } | null;
    };
    const pc = sessionWithDescription.sessionDescriptionHandler?.peerConnection;
    if (!pc) return;
    const remoteStream = new MediaStream();
    pc.getReceivers().forEach((receiver: RTCRtpReceiver) => {
      if (receiver.track) remoteStream.addTrack(receiver.track);
    });
    this.sipAudioElement.srcObject = remoteStream;
    void this.sipAudioElement.play().catch(() => {
      /* autoplay blocked — agent will hear once a user gesture occurs */
    });
  }

  private async sipDial(
    toRaw: string,
    callerId: string | undefined,
    callId: string | undefined,
  ): Promise<{ ok: boolean; detail?: string }> {
    const ready = await this.ensureSipRegistered();
    if (!ready.ok) return ready;
    // Registration can take a moment on the first VP call of a session — the
    // agent may have given up in that window. Past this point attachSipSession
    // has a handle, so hangup() can tear the session down on its own.
    if (this.isDialCanceled()) {
      return { ok: false, detail: 'Call ended before it connected' };
    }
    this.sipActiveCallId = callId ?? null;
    if (this.sipMockMode) {
      // As with Twilio mock: no session attaches, hangup() clears the timers.
      this.awaitingProviderSession = false;
      this.emit({ type: 'connecting' });
      const t = setTimeout(() => {
        this.emit({ type: 'connected' });
        if (this.sipActiveCallId) {
          void api.post(`/calls/${this.sipActiveCallId}/answered`, {}).catch(() => null);
        }
      }, 1500);
      this.mockTimers.push(t);
      return { ok: true };
    }
    if (!this.sipUserAgent) return { ok: false, detail: 'SIP user agent not initialised' };

    const target = UserAgent.makeURI(`sip:${toRaw}@${this.sipUserAgent.configuration.uri.host}`);
    if (!target) return { ok: false, detail: 'Invalid SIP target URI' };

    // Per-call caller-ID and call-ID are conveyed via custom SIP headers that
    // the dialplan's [from-agent] context reads via PJSIP_HEADER. Caller-ID
    // is applied before Dial(); call-ID names the MixMonitor recording file.
    const extraHeaders = [
      ...(callerId ? [`X-Marnic-Caller-Id: ${callerId}`] : []),
      ...(callId ? [`X-Marnic-Call-Id: ${callId}`] : []),
    ];
    const inviter = new Inviter(this.sipUserAgent, target, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      extraHeaders,
    });
    this.attachSipSession(inviter);
    try {
      await inviter.invite();
      return { ok: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'SIP invite failed';
      return { ok: false, detail: message };
    }
  }

  /** Initiate an outbound call. The backend creates the Call row + provider
   *  artefacts (Twilio call SID or Asterisk channel ID); the matching browser
   *  backend then bridges audio. In mock mode we simulate the lifecycle. */
  async dial(opts: DialOptions): Promise<DialResult> {
    this.awaitingProviderSession = true;
    this.dialCanceledAt = null;
    try {
      const result = await this.performDial(opts);
      // A failed dial never produces a session, so stop treating a later
      // hangup as a cancel of this one. A successful dial keeps the flag until
      // attachCall / attachSipSession takes over — for Twilio that is the
      // whole ring window, which is precisely where a press had no effect.
      if (!result.ok) this.awaitingProviderSession = false;
      return result;
    } catch (err) {
      this.awaitingProviderSession = false;
      throw err;
    }
  }

  private async performDial(opts: DialOptions): Promise<DialResult> {
    const store = useSoftphone.getState();
    const callerId = (opts.callerId ?? store.selectedCallerId) || undefined;
    const callerProvider = opts.callerProvider ?? store.selectedCallerProvider ?? undefined;
    const failureContext = `Could not place call [dial-debug selected=${callerId ?? 'auto'} provider=${callerProvider ?? 'auto'} to=${opts.toRaw}]`;
    console.log('[softphone] dial start', { toRaw: opts.toRaw, callerProvider });

    // Both backends need a working microphone. Mock-mode skips the prompt
    // because no audio actually flows.
    const usingMock = callerProvider === 'VOIPLINE' ? this.sipMockMode : this.mockMode;
    if (!usingMock) {
      const mic = await this.ensureMicrophoneAvailable();
      if (!mic.ok) {
        this.emit({ type: 'error', message: mic.detail ?? 'Microphone is unavailable' });
        return { ok: false, reason: 'MICROPHONE_UNAVAILABLE', detail: mic.detail };
      }
    }

    // Optimistic UI: advance the dialer out of DialPad the moment the agent
    // hits Call. Without this, if the server's call_started socket event is
    // delayed (or the SIP listener races with invite()), the agent sits on
    // DialPad while audio is already bridging and has no idea the call is up.
    // Reset to 'ready' on any pre-INVITE failure path below.
    const priorStatus = store.status;
    if (priorStatus === 'ready' || priorStatus === 'wrap_up') {
      useSoftphone.getState().setStatus('connecting');
    }
    const restoreStatusOnFailure = (): void => {
      const cur = useSoftphone.getState();
      if (cur.status === 'connecting' && !cur.currentCall) cur.setStatus('ready');
    };

    const result = await api.post<{
      ok: boolean;
      callId?: string;
      twilioCallSid?: string;
      reason?: string;
      detail?: string;
    }>('/calls/initiate', {
      ...opts,
      callerId,
      callerProvider,
      deviceId: getDeviceId(),
    }).catch((err: unknown) => ({
      ok: false,
      reason: 'CALL_FAILED',
      detail: extractApiErrorMessage(err, failureContext),
    }));

    if (!result.ok && !result.detail && !result.reason) {
      restoreStatusOnFailure();
      return {
        ok: false,
        reason: 'CALL_FAILED',
        detail: `Call failed before a provider returned an error. Selected caller: ${callerId ?? 'auto'} (${callerProvider ?? 'auto'})`,
      };
    }
    if (!result.ok) {
      restoreStatusOnFailure();
      return result;
    }

    // The agent hit End call while /calls/initiate was in flight. There was no
    // session to tear down at that point, so honour the cancel here — end the
    // Call row the server just created and never bridge audio. hangup() has
    // already emitted 'disconnected', so the UI needs nothing more.
    if (this.isDialCanceled()) {
      const pendingCallId = 'callId' in result ? result.callId : undefined;
      if (pendingCallId) {
        void api.post(`/calls/${pendingCallId}/end`).catch((err) => {
          console.warn('[softphone] failed to end a call cancelled mid-dial', err);
        });
      }
      restoreStatusOnFailure();
      return { ok: false, reason: CANCELED_BEFORE_CONNECT, detail: 'Call ended before it connected' };
    }

    if (callerProvider === 'VOIPLINE') {
      // Browser is the SIP UA — INVITE the prospect via Asterisk's WSS
      // transport. The dialplan's [from-agent] context dials the VP
      // trunk and bridges audio. We pass the chosen caller-ID through a
      // custom header so the dialplan can apply it before Dial().
      // result.ok is true here (narrowed above), but the union TS sees still
      // includes the catch shape without callId. Pull it out with an `in`.
      const initiateCallId = 'callId' in result ? result.callId : undefined;
      const sip = await this.sipDial(opts.toRaw, callerId, initiateCallId);
      if (!sip.ok) {
        restoreStatusOnFailure();
        // An agent who hit End call during SIP registration didn't hit a
        // fault — the caller must not report one.
        return {
          ok: false,
          reason: this.isDialCanceled() ? CANCELED_BEFORE_CONNECT : 'SIP_DIAL_FAILED',
          detail: sip.detail,
        };
      }
      return result;
    } else if (this.mockMode) {
      // No provider session ever attaches in mock — hangup() ends the
      // simulated call by clearing the timers below.
      this.awaitingProviderSession = false;
      this.emit({ type: 'connecting' });
      const t1 = setTimeout(() => this.emit({ type: 'connected' }), 1500);
      this.mockTimers.push(t1);
    } else if (this.device) {
      // The TwiML connects the prospect's PSTN leg back to staff_<id> so the
      // browser receives an `incoming` from Twilio which we auto-accept.
      this.emit({ type: 'connecting' });
    }

    return result;
  }

  acceptIncoming(): void {
    if (this.call) {
      this.call.accept();
      this.attachCall(this.call);
      return;
    }
    if (this.sipPendingInvitation) {
      const invitation = this.sipPendingInvitation;
      this.sipPendingInvitation = null;
      this.attachSipSession(invitation);
      void invitation.accept({
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      }).catch((err: unknown) => {
        console.warn('[softphone] SIP invitation accept failed', err);
      });
    }
  }

  rejectIncoming(): void {
    if (this.call) {
      this.call.reject();
      this.call = null;
      return;
    }
    if (this.sipPendingInvitation) {
      const invitation = this.sipPendingInvitation;
      this.sipPendingInvitation = null;
      const callId = this.sipActiveCallId;
      this.sipActiveCallId = null;
      void invitation.reject().catch((err: unknown) => {
        console.warn('[softphone] SIP invitation reject failed', err);
      });
      // Tell the server to mark the call ended — the SIP 603 will also
      // tear the channel down, but POSTing /end keeps the Call row state
      // tight even if the SIP signalling lags.
      if (callId) {
        void api.post(`/calls/${callId}/end`).catch((err) => {
          console.warn('[softphone] failed to POST /calls/:id/end on reject', err);
        });
      }
      // Use incoming_canceled (not disconnected) so the UI clears the
      // ringing state without opening the disposition modal — there is no
      // call to disposition when the agent declined to answer.
      this.emit({ type: 'incoming_canceled' });
    }
  }

  private attachCall(call: TwilioCall): void {
    // Belt-and-braces: if a previous call's listeners are still on the prior
    // TwilioCall object, drop them before binding the new one. attachCall is
    // invoked once per dial AND once per inbound (auto-accepted) bridge, so
    // back-to-back calls would otherwise stack handlers across instances.
    this.detachCall();
    this.call = call;
    // A real handle exists now — hangup() can disconnect it directly.
    this.awaitingProviderSession = false;
    const bind = (event: string, handler: (...args: unknown[]) => void): void => {
      (call as unknown as { on: (e: string, h: (...a: unknown[]) => void) => void }).on(event, handler);
      this.callHandlers.push([event, handler]);
    };
    bind('accept', () => this.emit({ type: 'connected' }));
    bind('disconnect', () => {
      this.detachCall();
      this.emit({ type: 'disconnected' });
    });
    bind('reject', () => {
      this.detachCall();
      this.emit({ type: 'disconnected', reason: 'rejected' });
    });
    bind('cancel', () => {
      this.detachCall();
      this.emit({ type: 'disconnected', reason: 'canceled' });
    });
    bind('error', (...args: unknown[]) => {
      const err = args[0] as { message?: string } | undefined;
      this.emit({ type: 'error', message: err?.message ?? 'Twilio call error' });
    });
    bind('mute', (...args: unknown[]) => {
      this.emit({ type: 'mute_changed', muted: !!args[0] });
    });
  }

  private detachCall(): void {
    const prior = this.call;
    if (prior) {
      const target = prior as unknown as { off?: (e: string, h: (...a: unknown[]) => void) => void };
      for (const [event, handler] of this.callHandlers) {
        try { target.off?.(event, handler); } catch { /* ignore */ }
      }
    }
    this.callHandlers = [];
    this.call = null;
  }

  /** End the active call. Everything here is synchronous and unconditional:
   *  the agent's exit from the call must never be downstream of a network
   *  round-trip. Previously the `disconnected` emit sat after
   *  `await api.post(/calls/:id/end)`, so any 5xx/401/offline blip skipped it
   *  and left the dialer pinned to a call whose media was already gone — with
   *  a second press unable to help, since both provider handles were null by
   *  then. /calls/:id/end is idempotent, so it is fire-and-forget. */
  async hangup(callId?: string): Promise<void> {
    if (this.mockMode || this.sipMockMode) {
      this.clearMockTimers();
    }

    // No provider session yet — the dial is still round-tripping, or Twilio
    // hasn't bridged the staff leg back. Record the intent so dial()/sipDial()
    // abort and the `incoming` handler rejects the bridge, instead of
    // connecting a call the agent has already given up on.
    if (this.awaitingProviderSession) this.dialCanceledAt = Date.now();

    // The click closure can hold a stale (or absent) call id — during the
    // optimistic 'connecting' window `call_started` may not have landed yet.
    // Fall back to whatever the service and store actually know about.
    const targetCallId = callId ?? this.sipActiveCallId ?? useSoftphone.getState().currentCall?.id;

    // Twilio's own 'disconnect' event fires asynchronously, so detachCall()
    // always removes the handler before it arrives — it can never be the thing
    // that moves the UI on. The unconditional emit below is the only path.
    this.call?.disconnect();
    this.detachCall();

    this.terminateSipSession();
    this.stopRemoteAudioPlayback();
    this.sipActiveCallId = null;
    this.sipMuted = false;

    this.emit({ type: 'disconnected' });

    if (targetCallId) {
      void api.post(`/calls/${targetCallId}/end`).catch((err) => {
        console.warn('[softphone] failed to POST /calls/:id/end on hangup', err);
      });
    }
  }

  mute(muted: boolean): void {
    // Per-provider, same reasoning as sendDigits: a combined mock guard here
    // meant a live Twilio call couldn't be muted whenever the SIP backend was
    // in mock mode. A live Twilio Call only exists outside Twilio mock mode.
    if (this.call && !this.mockMode) this.call.mute(muted);
    if (!this.sipMockMode && this.sipSession?.state === SessionState.Established) {
      const sessionWithDescription = this.sipSession as Session & {
        sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection } | null;
      };
      const pc = sessionWithDescription.sessionDescriptionHandler?.peerConnection;
      pc?.getSenders().forEach((sender: RTCRtpSender) => {
        if (sender.track && sender.track.kind === 'audio') sender.track.enabled = !muted;
      });
      this.sipMuted = muted;
    }
    // Always reflect the requested state in the UI. Twilio also fires its own
    // 'mute' event (see attachCall) which re-emits this, but a duplicate
    // mute_changed is harmless — setMuted is idempotent.
    this.emit({ type: 'mute_changed', muted });
  }

  sendDigits(digits: string): void {
    // Gate each provider leg on ITS OWN mock flag. A combined
    // `mockMode || sipMockMode` guard here silently swallowed Twilio DTMF
    // whenever the SIP backend was in mock mode — which it is for any agent
    // without VP credentials (ensureSipRegistered sets sipMockMode=true on
    // init). A live Twilio Call only exists outside Twilio mock mode, so the
    // optional-chaining no-ops on its own in mock; no extra guard needed.
    this.call?.sendDigits(digits);
    if (!this.sipMockMode && this.sipSession?.state === SessionState.Established) {
      // Reach into the WebRTC peer connection's RTCDTMFSender directly so
      // we can set tone duration. sip.js's sendDtmf wrapper uses the 100ms
      // browser default, which older voicemail menus (especially the '#'
      // terminator) miss — they time out and route to operator. 250ms
      // matches the Twilio Voice SDK and the typical desk-phone behaviour.
      const sdh = (this.sipSession as Session & {
        sessionDescriptionHandler?: { peerConnection?: RTCPeerConnection } | null;
      }).sessionDescriptionHandler;
      const pc = sdh?.peerConnection;
      if (pc) {
        const audioSender = pc.getSenders().find((s: RTCRtpSender) => s.track?.kind === 'audio');
        const dtmfSender = (audioSender as { dtmf?: RTCDTMFSender } | undefined)?.dtmf;
        dtmfSender?.insertDTMF(digits, 250, 100);
      }
    }
  }

  isOnCall(): boolean {
    return !!this.call || !!this.sipSession;
  }

  isMockMode(): boolean {
    return this.mockMode;
  }

  isMuted(): boolean {
    return this.sipMuted;
  }

  destroy(): void {
    this.clearMockTimers();
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer);
    this.tokenRefreshTimer = null;
    this.call?.disconnect();
    this.detachCall();
    this.device?.destroy();
    this.device = null;
    void this.sipRegisterer?.unregister().catch(() => null);
    this.sipRegisterer = null;
    void this.sipUserAgent?.stop().catch(() => null);
    this.sipUserAgent = null;
    this.detachSipSession();
    this.sipSession = null;
    if (this.sipAudioElement) {
      this.sipAudioElement.remove();
      this.sipAudioElement = null;
    }
    this.listeners.clear();
  }

  private clearMockTimers(): void {
    this.mockTimers.forEach((t) => clearTimeout(t));
    this.mockTimers = [];
  }
}

export const softphoneService = new SoftphoneService();
